/* import __COLOCATED_TEMPLATE__ from './modal.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent-2 */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type PlaygroundApiService from 'embercom/services/playground-api';
import { tracked } from '@glimmer/tracking';
import type RouterService from '@ember/routing/router-service';
import type IntlService from 'embercom/services/intl';
import { ExtractionType } from 'embercom/lib/fin-playground/question-generation';
import { TrackedArray } from 'tracked-built-ins';
import { ResponseError } from 'embercom/lib/inbox/requests';

interface Args {
  onClose: () => void;
  questionLimit: number;
  isQuestionGenerationPossible: boolean;
  isQuestionGenerationRunningForApp: boolean;
}

class QuestionInput {
  @tracked text = '';

  constructor(text = '') {
    this.text = text;
  }
}

export type QuestionAdditionMethod = 'manual' | 'csv' | 'question-generation';

export default class Modal extends Component<Args> {
  @service declare playgroundApi: PlaygroundApiService;
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare intercomEventService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;

  @tracked groupName = '';
  @tracked questionAdditionMethod: QuestionAdditionMethod = this.args.isQuestionGenerationPossible
    ? 'question-generation'
    : 'manual';
  @tracked manuallyAddedQuestions: TrackedArray<QuestionInput> = new TrackedArray([
    new QuestionInput(),
  ]);
  @tracked file = null;
  @tracked fileIsValid = false;
  @tracked fileFailedToUpload = false;
  @tracked parsingErrorMessage = '';
  @tracked questionsFromCSV: string[] = [];

  get disableContinueButton() {
    return this.disableBasedOnManualAdd || this.disableBasedOnCSVUpload;
  }

  get disableBasedOnCSVUpload() {
    // Disable the continue button if file isn't valid
    // or if the file is valid but there are no questions in it
    return (
      this.questionAdditionMethod === 'csv' &&
      (!this.fileIsValid || this.questionsFromCSV.length === 0)
    );
  }

  get disableBasedOnManualAdd() {
    return (
      this.questionAdditionMethod === 'manual' &&
      (this.manuallyAddedQuestions.length === 0 ||
        !this.manuallyAddedQuestions.some((q) => q.text.trim()))
    );
  }

  get defaultGroupNames() {
    if (this.questionAdditionMethod === 'manual') {
      return this.intl.t('ai-agent.playground.default-group-names.manual');
    } else if (this.questionAdditionMethod === 'csv') {
      return this.intl.t('ai-agent.playground.default-group-names.csv');
    } else {
      return this.intl.t('ai-agent.playground.default-group-names.question-generation');
    }
  }

  @action
  setQuestionAdditionMethod(method: QuestionAdditionMethod) {
    this.questionAdditionMethod = method;
  }

  @action updateManuallyAddedQuestions(questions: TrackedArray<QuestionInput>) {
    this.manuallyAddedQuestions = questions;
  }

  @action
  async createGroupAndCreateQuestions() {
    try {
      if (this.questionAdditionMethod === 'manual') {
        await this.createGroup(
          undefined,
          this.manuallyAddedQuestions.map((q) => q.text),
        );
      } else if (this.questionAdditionMethod === 'csv') {
        await this.createGroup(undefined, this.questionsFromCSV);
      } else if (this.questionAdditionMethod === 'question-generation') {
        await this.createGroup(ExtractionType.All);
      }
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.playground.banners.creating-group.question-creation-error'),
      );
    }
  }

  @action
  async setFile(file: any) {
    this.file = file;
    this.fileIsValid = file !== null;
    this.questionsFromCSV = await this.parseCsvFile();
  }

  @action
  async parseCsvFile(): Promise<string[]> {
    if (!this.file) {
      return [];
    }

    try {
      this.parsingErrorMessage = '';
      let results = await this.playgroundApi.parseCsvFile(this.file);

      if (results.data.length === 0) {
        throw new Error(
          this.intl.t('ai-agent.playground.upload-questions-modal.fallback-processing-error'),
        );
      }

      // For the Alpha release, we only support a single column of questions
      let questions = (results as { data: string[] }).data.map((row: any) => row[0]);
      return questions;
    } catch (error) {
      this.fileFailedToUpload = true;

      if (error instanceof ResponseError) {
        this.parsingErrorMessage = this.intl.t(
          'ai-agent.playground.call-error.upload-questions.message',
        );
      } else {
        this.parsingErrorMessage =
          error.message ||
          this.intl.t('ai-agent.playground.upload-questions-modal.fallback-processing-error');
        this.fileIsValid = false;
      }
      return [];
    }
  }

  @action
  async createGroup(extractionType?: ExtractionType, questions?: string[]) {
    let groupName = this.groupName?.trim() ? this.groupName : this.defaultGroupNames;

    let createdGroupSummary = await this.playgroundApi.createPlaygroundGroup(
      groupName,
      [],
      undefined,
      extractionType,
      questions,
    );

    this.intercomEventService.trackAnalyticsEvent({
      action: 'added_questions',
      object: 'playground',
      new_question_count: questions?.length ?? 0,
      source: this.questionAdditionMethod === 'manual' ? 'add_questions_form' : 'csv_upload',
    });

    this.args.onClose();

    // Navigate to the test route with the new group ID
    // We use currentRouteName to account for user being on the standalone testing route
    this.router.transitionTo(this.router.currentRouteName, createdGroupSummary.id);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Playground::Modals::NewGroupModal::Modal': typeof Modal;
    'ai-agent/playground/modals/new-group-modal/modal': typeof Modal;
  }
}
